import React, { useCallback, useRef } from 'react';
import { FieldError, useFormContext, Controller } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import cn from 'classnames';
import { Typography, Tag } from 'antd';
// Helpers
import { AWS_MEDIACONVERT_VIDEO_FORMATS } from 'helpers/videoFormats';
// Ui
import { warningNotification } from 'ui/Notification';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
// Styles
import styles from './UploadTimelineFilesInput.module.scss';

const { Text } = Typography;

type FileWithPreview = File & {
  preview: string;
};

type UploadScreenshotsProps = {
  fieldName: string;
  acceptFileTypes?: { [key: string]: string[] };
  inputDescription?: string;
  inputTitle?: string;
  tagIcon?: string;
  maxFiles?: number;
};

const UploadTimelineFilesInput = ({
  fieldName,
  acceptFileTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    ...AWS_MEDIACONVERT_VIDEO_FORMATS,
  },
  inputDescription = 'Upload a video (max 5GB) in supported formats (MP4, MOV, AVI, WebM, etc.) or an image in JPEG or PNG format.',
  inputTitle = 'Upload Files',
  maxFiles = 1,
}: UploadScreenshotsProps): JSX.Element => {
  const { control, getValues, watch, formState } = useFormContext();
  const onChangeRef = useRef<(value: any) => void>();
  const uploads = watch(fieldName);
  const errors = formState.errors;

  const fieldError =
    errors && fieldName
      ? fieldName
          .split('.')
          .reduce((acc, key) => (acc ? acc[key] : undefined), errors)
      : undefined;

  const handleFileRemove = (preview: string) => {
    const uploads = getValues(fieldName);

    const newUploads: FileWithPreview[] = [];

    uploads?.forEach((item: any) => {
      if ('url' in item) {
        if (item.key !== preview) {
          newUploads.push(item);
        }
      } else {
        if (item.preview !== preview) {
          newUploads.push(item);
        }
      }
    });
    onChangeRef.current?.(newUploads);
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const existingUploads = getValues(fieldName);

      if (
        (existingUploads?.length ?? 0) + (acceptedFiles?.length ?? 0) <=
        maxFiles
      ) {
        const filesWithPreview = acceptedFiles.map((item: File) =>
          Object.assign(item, {
            preview: URL.createObjectURL(item),
          })
        );

        onChangeRef.current?.([
          ...(existingUploads ? existingUploads : []),
          ...filesWithPreview,
        ]);
      } else {
        warningNotification(
          `${maxFiles} files maximum for upload. You are trying to upload ${acceptedFiles.length} files`,
          'File Limit Exceeded'
        );
      }
    },
    [getValues, maxFiles, fieldName]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: Object.values(acceptFileTypes).flat(),
  });

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ onChange, onBlur, ref }) => {
        onChangeRef.current = onChange;
        return (
          <>
            <div className={styles.uploadedFiles}>
              <div
                className={cn(styles.uploadWrapper, {
                  [styles.error]: fieldError,
                })}
                {...getRootProps()}
              >
                <UploadOutlined />
                <input
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  {...getInputProps()}
                />
                <div>
                  <Text>{inputTitle}</Text>
                  <Text type="secondary">{inputDescription}</Text>
                </div>
              </div>
              {fieldError && (
                <p className={styles.error}>
                  {(fieldError as unknown as FieldError)?.message}
                </p>
              )}

              <div className={styles.uploadedFiles}>
                {uploads &&
                  uploads.map(
                    (
                      upload:
                        | FileWithPreview
                        | { url: string; key: string; name: string },
                      index: React.Key | null | undefined
                    ) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() =>
                          handleFileRemove(
                            'url' in upload ? upload.key : upload.preview ?? ''
                          )
                        }
                        icon={<CameraOutlined />}
                      >
                        {'url' in upload ? upload.key : upload.name}
                      </Tag>
                    )
                  )}
              </div>
            </div>
          </>
        );
      }}
    />
  );
};

export default UploadTimelineFilesInput;
